import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const REVIEWS = [
  {
    name: 'Chris Thompson',
    role: 'Local Guide · 56 reviews',
    rating: 5,
    date: '7 months ago',
    text: 'Quantum Electrical did a full rewire on our bungalow. Lee firstly did an electrical condition report whilst we were considering purchasing the property and was very accommodating with the ups and downs of conveyancing and settling on dates. Everything was completed to our satisfaction and within the timescale Lee promised. Well done Lee, and also a big thank you to Danielle who cleaned the property.',
  },
  {
    name: 'Keith Clarke',
    role: '1 review',
    rating: 5,
    date: '7 months ago',
    text: 'I have used Lee at Quantum Electrical Services to supply several new wall sockets, fit new spot lights in the kitchen and bathroom and to install an extractor fan in the bathroom and a new cooker hood. This was over several visits and every job was completed efficiently, cleanly and professionally. Always on time, always courteous. Would highly recommend.',
  },
  {
    name: 'Andy Fisk',
    role: '6 reviews',
    rating: 5,
    date: '8 months ago',
    text: "From start to finish the work Lee has quoted for and delivered has been first class. It's good to finally find a sparky that returns messages! Our work was to install new ceiling lights, move and install plugs and fit new plinth lighting. Completed with minimal damage and mess. Lee takes real pride in his work — cannot recommend highly enough.",
  },
  {
    name: 'Karen Brine',
    role: '6 reviews',
    rating: 5,
    date: 'A year ago',
    text: 'Very happy with the work completed by Lee at Quantum Electrical Services. We moved into our house last year and had a list of electrical work required. A long list of small jobs. Lee came out to quote quickly, he provided a detailed quote and completed the work to an excellent standard. Friendly, professional and trustworthy. Would not hesitate to recommend.',
  },
  {
    name: 'Roy L77',
    role: 'Local Guide · 57 reviews · 172 photos',
    rating: 5,
    date: 'A year ago',
    text: 'Lee completed a small job for us installing some new sockets and completed it quickly, efficiently and professionally. From initial quote through to final production of the required certification, I just cannot praise him enough. It\'s actually quite a rarity to find a tradesman who does exactly what he says, when he says it. Highly recommended.',
  },
  {
    name: 'Paul Varney',
    role: '1 review',
    rating: 5,
    date: '8 months ago',
    text: "Lee was extremely efficient, friendly and instantly knew the problem with our Solar Control Unit. He sorted the problem out with a replacement in double quick time. The apprentice that accompanied him will have an excellent teacher. Lee's knowledge and expertise is clearly first class. Thanks Lee — brilliant service as always.",
  },
];

export default function Reviews() {
  const [current, setCurrent] = useState(0);
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();

  const prev = () => setCurrent((c) => (c === 0 ? REVIEWS.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === REVIEWS.length - 1 ? 0 : c + 1));

  const visible = [
    REVIEWS[current],
    REVIEWS[(current + 1) % REVIEWS.length],
    REVIEWS[(current + 2) % REVIEWS.length],
  ];

  return (
    <section id="reviews" className="py-24 bg-[#071120] relative overflow-hidden">
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full opacity-10 blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #C8A96A, transparent)' }}
      />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={headRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">What Clients Say</p>
          <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-tight">
            Real Reviews From{' '}
            <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">
              Real People
            </span>
          </h2>
          {/* Google stars */}
          <div className="flex items-center justify-center gap-2 mt-5">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <i key={s} className="ri-star-fill text-[#C8A96A] text-xl" />
              ))}
            </div>
            <span className="text-white/70 text-sm ml-1">5.0 on Google Reviews</span>
          </div>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {visible.map((review, i) => (
            <ReviewCard key={`${review.name}-${i}`} review={review} index={i} />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={prev}
            className="w-12 h-12 flex items-center justify-center border border-[#C8A96A]/40 rounded-full text-[#C8A96A] hover:bg-[#C8A96A]/10 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
          </button>

          <div className="flex gap-2">
            {REVIEWS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === current ? 'w-8 h-2 bg-[#C8A96A]' : 'w-2 h-2 bg-[#C8A96A]/30 hover:bg-[#C8A96A]/60'
                }`}
              />
            ))}
          </div>

          <button
            onClick={next}
            className="w-12 h-12 flex items-center justify-center border border-[#C8A96A]/40 rounded-full text-[#C8A96A] hover:bg-[#C8A96A]/10 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line" />
          </button>
        </div>

        {/* Google link */}
        <div className="text-center mt-8">
          <a
            href="https://share.google/wBdMPYxVfjdefwkN9"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#C8A96A]/70 hover:text-[#C8A96A] text-sm transition-colors duration-300 cursor-pointer"
          >
            <i className="ri-google-fill" />
            View all reviews on Google
            <i className="ri-external-link-line text-xs" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({
  review,
  index,
}: {
  review: (typeof REVIEWS)[0];
  index: number;
}) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`bg-[#0D1E35] border border-white/5 hover:border-[#C8A96A]/30 rounded-2xl p-6 flex flex-col gap-4 transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms`, borderTop: '3px solid #C8A96A' }}
    >
      {/* Quote mark */}
      <span className="font-playfair text-5xl text-[#C8A96A]/30 leading-none select-none">&ldquo;</span>

      {/* Stars */}
      <div className="flex gap-0.5">
        {Array.from({ length: review.rating }).map((_, i) => (
          <i key={i} className="ri-star-fill text-[#C8A96A] text-sm" />
        ))}
      </div>

      {/* Text */}
      <p className="text-white/75 text-sm leading-relaxed flex-1">{review.text}</p>

      {/* Author */}
      <div className="flex items-center gap-3 pt-4 border-t border-white/10">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #C8A96A, #A8894F)' }}
        >
          {review.name[0]}
        </div>
        <div>
          <p className="text-white font-semibold text-sm">{review.name}</p>
          <p className="text-white/40 text-xs">{review.role} · {review.date}</p>
        </div>
      </div>
    </div>
  );
}
